import { Link } from 'react-router-dom';
import { useTenantGovernance } from '@/hooks/useTenantGovernance';
import { isPrivateBusinessPath } from '@/lib/business-privacy-navigation';
import { useEffect, useState } from 'react';
import { refreshDashboardSession } from '@/api/client';
import { storage } from '@/utils/storage';
import { Navigate, useLocation } from 'react-router-dom';
import { isDashboardTokenUsable, useAuthStore } from '@/store/authStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useProviderCapabilities } from '@/hooks/useProviderCapabilities';
import { isProviderPathAllowed } from '@/lib/provider-capabilities.mjs';
import { useEnabledModules } from '@/hooks/useEnabledModules';
import { EmptyState, PageLoader } from '@/components/ui';
import { isPortalRoleAllowed } from '@/config/portal';
import { isTenantPathRelevant } from '@/lib/tenant-capabilities.mjs';
import { useTranslation } from 'react-i18next';

// Auth guard component
export function ProtectedRoute({ children, loginReturn }: { children: React.ReactNode; loginReturn?: '/dispatcher' | '/field' }) {
  const { isAuthenticated, role } = useAuthStore();
  const token = storage.getAccessToken();
  const usable = isDashboardTokenUsable(token);
  const canRefresh = Boolean(token && storage.getRefreshToken() && isAuthenticated);
  const [refreshError, setRefreshError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { t } = useTranslation();
  useEffect(() => {
    if (usable || !canRefresh) return;
    let active = true;
    refreshDashboardSession().catch(() => { if (active) setRefreshError(true); });
    return () => { active = false; };
  }, [usable, canRefresh, attempt]);
  if (!usable && canRefresh) {
    if (refreshError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{ label: t('common.retry'), onClick: () => { setRefreshError(false); setAttempt(value => value + 1); } }} />;
    return <PageLoader />;
  }
  if (!usable || !isAuthenticated) return <Navigate to={loginReturn ? `/login?next=${loginReturn}` : '/login'} replace />;
  if (!isPortalRoleAllowed(role)) return <Navigate to="/login?reason=portal-role-mismatch" replace />;
  return <>{children}</>;
}

// Guest route (redirect if authenticated)
export function GuestRoute({ children }: { children: React.ReactNode }) {
  const { role } = useAuthStore();
  const location = useLocation();
  const denied = new URLSearchParams(location.search).has('reason');
  const token = localStorage.getItem('heglo_access_token');
  if (isDashboardTokenUsable(token) && isPortalRoleAllowed(role) && !denied) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// SuperAdmin only route guard
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useTenantContext();
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// ProviderAdmin only route guard
export function ProviderWorkspaceRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { role } = useAuthStore();
  const { providerAdminAllowed, ready, isError } = useEnabledModules();
  if (role !== 'provideradmin' && role !== 'providerstaff') return <Navigate to="/" replace />;
  if (isError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{label:t('common.retry'),onClick:()=>window.location.reload()}} />;
  if (!ready) return <PageLoader />;
  if (!providerAdminAllowed) return <Navigate to="/login?reason=provider-portal-disabled" replace />;
  return <>{children}</>;
}

export function ProviderAdminRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { role, isAuthenticated } = useAuthStore();
  const { providerAdminAllowed, ready, isError } = useEnabledModules();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role !== 'provideradmin') return <Navigate to="/" replace />;
  if (isError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{ label: t('common.retry'), onClick: () => window.location.reload() }} />;
  if (!ready) return <PageLoader />;
  if (!providerAdminAllowed) return <Navigate to="/login?reason=provider-portal-disabled" replace />;
  return <>{children}</>;
}

/** Applies the sidebar's additive capability policy to direct URLs. */
export function ProviderCapabilityRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const providerCapabilities = useProviderCapabilities();
  const path = location.pathname;

    if (providerCapabilities.staffDiscoveryLoading) return <PageLoader />;
    if (providerCapabilities.staffDiscoveryError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{ label: t('common.retry'), onClick: () => window.location.reload() }} />;
    if (providerCapabilities.isStaff) {
      if (providerCapabilities.isError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{ label: t('common.retry'), onClick: () => window.location.reload() }} />;
      if (!providerCapabilities.ready) return <PageLoader />;
      if (!providerCapabilities.capabilities.staffStock) return <EmptyState title={t('merchantStaff.noWorkspaceAccess')} />;
      if (path !== '/business/staff-stock') return <Navigate to="/business/staff-stock" replace />;
      return <>{children}</>;
    }

  // Dashboard and settings stay reachable to complete initial configuration.
  if (isProviderPathAllowed(path, {})) return <>{children}</>;
  if (providerCapabilities.isError) return <EmptyState title={t('common.errorLoadingData')} primaryAction={{ label: t('common.retry'), onClick: () => window.location.reload() }} />;
  if (!providerCapabilities.ready && !providerCapabilities.isError) return <PageLoader />;
  if (!providerCapabilities.isPathAllowed(path)) return <Navigate to="/business" replace />;
  return <>{children}</>;
}

// Tenant route guard - requires active tenant context
// SuperAdmin must impersonate a tenant first, TenantAdmin always has context
export function TenantRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const tenant = useEnabledModules();
  const governance = useTenantGovernance();
  const { t } = useTranslation();
  const { activeTenantId } = useTenantContext();
  const { role } = useAuthStore();
  // Provider-admins are confined to /business/* (ProviderAdminRoute).
  // useTenantContext maps provideradmin -> tenantadmin mode for data
  // scoping, but they must NOT operate the tenant marketplace console
  // (orders/customers/providers/catalog/config). Gate on the raw role
  // so the mode mapping is untouched.
  if (role === 'provideradmin') {
    return <Navigate to="/business" replace />;
  }
  if (role === 'superadmin' && !activeTenantId) {
    return <Navigate to="/tenants" replace />;
  }
  const isTenantRole = role === 'tenantadmin' || role === 'support';
  const isScopedSuperAdmin = role === 'superadmin' && activeTenantId != null;
  if ((!isTenantRole && !isScopedSuperAdmin) || activeTenantId == null) {
    return <div role="alert">{t('common.tenantAccessUnavailable')}</div>;
  }
  if (!tenant.ready || (governance.enabled && !governance.ready)) return <PageLoader />;
  const providerDetail = /^\/providers\/[^/]+$/.test(location.pathname) && !['/providers/register', '/providers/approval', '/providers/status', '/providers/debts'].includes(location.pathname);
  if (governance.independent && (isPrivateBusinessPath(location.pathname) || providerDetail)) return <div className="space-y-4 rounded-xl border p-6"><h1 className="text-xl font-bold">{t('governance.title')}</h1><p>{t('governance.approvalHelp')}</p><Link className="text-primary underline" to="/governance">{t('governance.request')}</Link></div>;
  if (!isTenantPathRelevant(location.pathname, tenant.capabilities)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Notification administration is limited to SuperAdmin and TenantAdmin.
// Support and ProviderAdmin must not edit delivery gateways or templates.
export function AdminOrTenantRoute({ children }: { children: React.ReactNode }) {
  const { activeTenantId } = useTenantContext();
  const { role } = useAuthStore();

  if (role === 'provideradmin') {
    return <Navigate to="/business" replace />;
  }

  if (role === 'superadmin') {
    return <>{children}</>;
  }

  if (role !== 'tenantadmin' || activeTenantId == null) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
